import React, { useState, useEffect, useCallback } from 'react';
import { Eye, FileDown, CreditCard, Pencil, Copy, Trash2, GraduationCap, Plus, BarChart3, CheckCircle, XCircle, AlertTriangle, Search, Coins, Calendar, Lightbulb, FileText, RefreshCw } from 'lucide-react';
import './DevisManagement.css';

const DevisManagement = ({ user, token, onBack }) => {
  const [devis, setDevis] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDevis, setEditingDevis] = useState(null);
  const [viewingDevis, setViewingDevis] = useState(null);
  const [stats, setStats] = useState(null);
  const [filterStatut, setFilterStatut] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentLinkModal, setPaymentLinkModal] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    date_validite: '',
    notes: '',
    conditions: 'Paiement à réception de facture. TVA non applicable, art. 293 B du CGI.',
    lignes: []
  });

  const fetchDevis = useCallback(async () => {
    try {
      const response = await fetch(`/api/devis`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDevis(data);
    } catch (error) {
      console.error('Erreur chargement devis:', error);
    }
  }, [token]);

  const fetchPrestations = useCallback(async () => {
    try {
      const response = await fetch(`/api/prestations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPrestations(data);
    } catch (error) {
      console.error('Erreur chargement prestations:', error);
    }
  }, [token]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await fetch(`/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setClients(data);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`/api/devis-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDevis(), fetchPrestations(), fetchClients(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDevis, fetchPrestations, fetchClients, fetchStats]);

  const openNewDevis = () => {
    const validite = new Date();
    validite.setDate(validite.getDate() + 30);

    setFormData({
      client_id: '',
      date_validite: validite.toISOString().split('T')[0],
      notes: '',
      conditions: 'Paiement à réception de facture.',
      lignes: [{
        description: '',
        prix_unitaire_ht: '',
        prix_mensuel_ht: '',
        quantite: 1,
        prestation_id: null,
        is_formation: false,
        formation_intitule: '',
        formation_convention_numero: '',
        formation_convention_date: '',
        formation_beneficiaires: '',
        formation_modalite: '',
        formation_duree_heures: '',
        formation_date_debut: '',
        formation_date_fin: ''
      }]
    });
    setEditingDevis(null);
    setShowModal(true);
  };

  const openEditDevis = async (devisItem) => {
    try {
      const response = await fetch(`/api/devis/${devisItem.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      setFormData({
        client_id: data.client_id,
        date_validite: data.date_validite.split('T')[0],
        notes: data.notes || '',
        conditions: data.conditions || '',
        lignes: data.lignes.map(l => ({
          description: l.description,
          prix_unitaire_ht: l.prix_unitaire_ht,
          prix_mensuel_ht: l.prix_mensuel_ht || '',
          quantite: l.quantite,
          prestation_id: l.prestation_id,
          is_formation: l.formation_intitule ? true : false,
          formation_intitule: l.formation_intitule || '',
          formation_convention_numero: l.formation_convention_numero || '',
          formation_convention_date: l.formation_convention_date ? l.formation_convention_date.split('T')[0] : '',
          formation_beneficiaires: l.formation_beneficiaires || '',
          formation_modalite: l.formation_modalite || '',
          formation_duree_heures: l.formation_duree_heures || '',
          formation_date_debut: l.formation_date_debut ? l.formation_date_debut.split('T')[0] : '',
          formation_date_fin: l.formation_date_fin ? l.formation_date_fin.split('T')[0] : ''
        }))
      });
      setEditingDevis(data);
      setShowModal(true);
    } catch (error) {
      console.error('Erreur chargement devis:', error);
    }
  };

  const viewDevisDetails = async (devisItem) => {
    try {
      const response = await fetch(`/api/devis/${devisItem.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setViewingDevis(data);
    } catch (error) {
      console.error('Erreur chargement devis:', error);
    }
  };

  const addLigne = () => {
    setFormData({
      ...formData,
      lignes: [...formData.lignes, {
        description: '',
        prix_unitaire_ht: '',
        prix_mensuel_ht: '',
        quantite: 1,
        prestation_id: null,
        is_formation: false,
        formation_intitule: '',
        formation_convention_numero: '',
        formation_convention_date: '',
        formation_beneficiaires: '',
        formation_modalite: '',
        formation_duree_heures: '',
        formation_date_debut: '',
        formation_date_fin: ''
      }]
    });
  };

  const removeLigne = (index) => {
    const newLignes = formData.lignes.filter((_, i) => i !== index);
    setFormData({ ...formData, lignes: newLignes });
  };

  const updateLigne = (index, field, value) => {
    const newLignes = [...formData.lignes];
    newLignes[index][field] = value;
    setFormData({ ...formData, lignes: newLignes });
  };

  const selectPrestation = (index, prestationId) => {
    const prestation = prestations.find(p => p.id === parseInt(prestationId));
    if (prestation) {
      const newLignes = [...formData.lignes];
      newLignes[index] = {
        ...newLignes[index],
        prestation_id: prestation.id,
        description: prestation.nom,
        prix_unitaire_ht: prestation.prix_ht,
        prix_mensuel_ht: prestation.prix_mensuel_ht || '',
        is_formation: prestation.is_formation || false,
        formation_intitule: prestation.is_formation ? prestation.nom : '',
        formation_convention_numero: '',
        formation_convention_date: '',
        formation_beneficiaires: '',
        formation_modalite: '',
        formation_duree_heures: '',
        formation_date_debut: '',
        formation_date_fin: ''
      };
      setFormData({ ...formData, lignes: newLignes });
    }
  };

  const calculateTotals = () => {
    let totalHT = 0;
    let totalMensuelHT = 0;

    formData.lignes.forEach(ligne => {
      const quantite = parseFloat(ligne.quantite) || 1;
      totalHT += (parseFloat(ligne.prix_unitaire_ht) || 0) * quantite;
      totalMensuelHT += (parseFloat(ligne.prix_mensuel_ht) || 0) * quantite;
    });

    const tva = totalHT * 0.20;
    const totalTTC = totalHT + tva;
    const tvaMensuel = totalMensuelHT * 0.20;
    const totalMensuelTTC = totalMensuelHT + tvaMensuel;

    return { totalHT, tva, totalTTC, totalMensuelHT, tvaMensuel, totalMensuelTTC };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.client_id) {
      alert('Veuillez sélectionner un client');
      return;
    }

    if (formData.lignes.length === 0 || !formData.lignes.some(l => l.description && l.prix_unitaire_ht)) {
      alert('Veuillez ajouter au moins une ligne');
      return;
    }

    try {
      const url = editingDevis
        ? `/api/devis/${editingDevis.id}`
        : `/api/devis`;

      const method = editingDevis ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: formData.client_id,
          date_validite: formData.date_validite,
          notes: formData.notes,
          conditions: formData.conditions,
          lignes: formData.lignes.filter(l => l.description && l.prix_unitaire_ht)
        })
      });

      if (response.ok) {
        setShowModal(false);
        fetchDevis();
        fetchStats();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde devis:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const updateStatut = async (devisId, newStatut) => {
    try {
      const response = await fetch(`/api/devis/${devisId}/statut`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ statut: newStatut })
      });

      if (response.ok) {
        fetchDevis();
        fetchStats();
      }
    } catch (error) {
      console.error('Erreur changement statut:', error);
    }
  };

  const deleteDevis = async (devisId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) return;

    try {
      const response = await fetch(`/api/devis/${devisId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchDevis();
        fetchStats();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur suppression devis:', error);
    }
  };

  const downloadPDF = async (devisId, numeroDevis) => {
    try {
      const response = await fetch(`/api/devis/${devisId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${numeroDevis}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error);
    }
  };

  const duplicateDevis = async (devisId) => {
    try {
      const response = await fetch(`/api/devis/${devisId}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchDevis();
        fetchStats();
        alert('Devis dupliqué avec succès');
      }
    } catch (error) {
      console.error('Erreur duplication:', error);
    }
  };

  const generateStripePaymentLink = async (devisItem) => {
    setGeneratingLink(devisItem.id);
    try {
      const response = await fetch(`/api/devis/${devisItem.id}/stripe-payment-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentLinkModal({
          link: data.payment_link,
          devisNumero: data.devis_numero,
          montantPonctuel: data.montant_ponctuel,
          montantMensuel: data.montant_mensuel
        });
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur lors de la génération du lien');
      }
    } catch (error) {
      console.error('Erreur génération lien Stripe:', error);
      alert('Erreur lors de la génération du lien de paiement');
    } finally {
      setGeneratingLink(null);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Lien copié dans le presse-papiers !');
    } catch (error) {
      // Fallback pour les navigateurs plus anciens
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Lien copié dans le presse-papiers !');
    }
  };

  const getStatutBadge = (statut) => {
    const badges = {
      brouillon: { class: 'badge-draft', label: 'Brouillon' },
      envoye: { class: 'badge-sent', label: 'Envoyé' },
      accepte: { class: 'badge-accepted', label: 'Accepté' },
      refuse: { class: 'badge-refused', label: 'Refusé' },
      expire: { class: 'badge-expired', label: 'Expiré' }
    };
    const badge = badges[statut] || badges.brouillon;
    return <span className={`devis-badge ${badge.class}`}>{badge.label}</span>;
  };

  const filteredDevis = devis.filter(d => {
    const matchStatut = filterStatut === 'all' || d.statut === filterStatut;
    const matchSearch = searchTerm === '' ||
      d.numero_devis?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.client_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.client_entreprise?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchStatut && matchSearch;
  });

  const { totalHT, tva, totalTTC, totalMensuelHT, tvaMensuel, totalMensuelTTC } = calculateTotals();

  if (loading) {
    return (
      <div className="devis-management">
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="devis-management">
      <div className="devis-header">
        <div className="header-left">
          <button className="btn-back" onClick={onBack}>
            ← Retour
          </button>
          <h1>Gestion des Devis</h1>
        </div>
        <button className="btn-primary" onClick={openNewDevis}>
          + Nouveau Devis
        </button>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="devis-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.total_devis || 0}</div>
            <div className="stat-label">Total Devis</div>
          </div>
          <div className="stat-card stat-draft">
            <div className="stat-value">{stats.brouillons || 0}</div>
            <div className="stat-label">Brouillons</div>
          </div>
          <div className="stat-card stat-sent">
            <div className="stat-value">{stats.envoyes || 0}</div>
            <div className="stat-label">Envoyés</div>
          </div>
          <div className="stat-card stat-accepted">
            <div className="stat-value">{stats.acceptes || 0}</div>
            <div className="stat-label">Acceptés</div>
          </div>
          <div className="stat-card stat-amount">
            <div className="stat-value">{parseFloat(stats.montant_accepte || 0).toLocaleString('fr-FR')} €</div>
            <div className="stat-label">Montant accepté</div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="devis-filters">
        <input
          type="text"
          placeholder="Rechercher..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          className="filter-select"
        >
          <option value="all">Tous les statuts</option>
          <option value="brouillon">Brouillons</option>
          <option value="envoye">Envoyés</option>
          <option value="accepte">Acceptés</option>
          <option value="refuse">Refusés</option>
          <option value="expire">Expirés</option>
        </select>
      </div>

      {/* Liste des devis */}
      <div className="devis-list">
        {filteredDevis.length === 0 ? (
          <div className="empty-state">
            <p>Aucun devis trouvé</p>
            <button className="btn-primary" onClick={openNewDevis}>Créer votre premier devis</button>
          </div>
        ) : (
          <table className="devis-table">
            <thead>
              <tr>
                <th>N° Devis</th>
                <th>Client</th>
                <th>Date</th>
                <th>Validité</th>
                <th>Montant TTC</th>
                <th>Abonnement/mois</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDevis.map(d => (
                <tr key={d.id}>
                  <td className="devis-numero">{d.numero_devis}</td>
                  <td>
                    <div className="client-info">
                      <span className="client-name">{d.client_nom}</span>
                      {d.client_entreprise && <span className="client-company">{d.client_entreprise}</span>}
                    </div>
                  </td>
                  <td>{new Date(d.date_emission).toLocaleDateString('fr-FR')}</td>
                  <td>{new Date(d.date_validite).toLocaleDateString('fr-FR')}</td>
                  <td className="montant">{parseFloat(d.total_ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</td>
                  <td className="montant-mensuel">
                    {parseFloat(d.total_mensuel_ttc || 0) > 0
                      ? `${parseFloat(d.total_mensuel_ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €/mois`
                      : '-'}
                  </td>
                  <td>{getStatutBadge(d.statut)}</td>
                  <td className="actions-cell">
                    <button className="btn-icon" onClick={() => viewDevisDetails(d)} title="Voir">
                      <Eye size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                    </button>
                    <button className="btn-icon" onClick={() => downloadPDF(d.id, d.numero_devis)} title="Télécharger PDF">
                      <FileDown size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                    </button>
                    <button
                      className="btn-icon btn-stripe"
                      onClick={() => generateStripePaymentLink(d)}
                      title="Créer lien de paiement Stripe"
                      disabled={generatingLink === d.id}
                    >
                      {generatingLink === d.id ? '⏳' : <CreditCard size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />}
                    </button>
                    {(user.role === 'admin' || d.statut === 'brouillon') && (
                      <button className="btn-icon" onClick={() => openEditDevis(d)} title="Modifier">
                        <Pencil size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                      </button>
                    )}
                    <button className="btn-icon" onClick={() => duplicateDevis(d.id)} title="Dupliquer">
                      <Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                    </button>
                    {(user.role === 'admin' || d.statut === 'brouillon') && (
                      <button className="btn-icon btn-danger" onClick={() => deleteDevis(d.id)} title="Supprimer">
                        <Trash2 size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                      </button>
                    )}
                    <button className="btn-small btn-download" onClick={() => downloadPDF(d.id, d.numero_devis)}>
                      Télécharger
                    </button>
                    {d.statut === 'brouillon' && (
                      <button className="btn-small btn-send" onClick={() => updateStatut(d.id, 'envoye')}>
                        Envoyer
                      </button>
                    )}
                    {d.statut === 'envoye' && (
                      <>
                        <button className="btn-small btn-accept" onClick={() => updateStatut(d.id, 'accepte')}>
                          Accepter
                        </button>
                        <button className="btn-small btn-refuse" onClick={() => updateStatut(d.id, 'refuse')}>
                          Refuser
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal création/édition */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h2>{editingDevis ? 'Modifier le devis' : 'Nouveau devis'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Sélection client */}
                <div className="form-row">
                  <div className="form-group flex-2">
                    <label>Client *</label>
                    <select
                      value={formData.client_id}
                      onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                      required
                    >
                      <option value="">Sélectionner un client</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.nom} {c.entreprise ? `(${c.entreprise})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Date de validité</label>
                    <input
                      type="date"
                      value={formData.date_validite}
                      onChange={(e) => setFormData({ ...formData, date_validite: e.target.value })}
                    />
                  </div>
                </div>

                {/* Lignes du devis */}
                <div className="devis-lignes-section">
                  <div className="section-header">
                    <h3>Prestations</h3>
                    <button type="button" className="btn-add-ligne" onClick={addLigne}>
                      + Ajouter une ligne
                    </button>
                  </div>

                  {formData.lignes.length > 0 && (
                    <div className="ligne-row ligne-header-row">
                      <div className="ligne-header-label">Prestation</div>
                      <div className="ligne-header-label">Nom affiché (modifiable)</div>
                      <div className="ligne-header-label">Qté</div>
                      <div className="ligne-header-label">Prix HT</div>
                      <div className="ligne-header-label">Abo/mois</div>
                      <div className="ligne-header-label">Total</div>
                      <div></div>
                    </div>
                  )}

                  {formData.lignes.map((ligne, index) => (
                    <div key={index} className="ligne-row">
                      <div className="ligne-prestation">
                        <select
                          value={ligne.prestation_id || ''}
                          onChange={(e) => selectPrestation(index, e.target.value)}
                        >
                          <option value="">-- Prestation prédéfinie --</option>
                          {prestations.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.is_formation ? '🎓 ' : ''}{p.nom} ({parseFloat(p.prix_ht).toFixed(2)} € HT{p.prix_mensuel_ht ? ` + ${parseFloat(p.prix_mensuel_ht).toFixed(2)} €/mois` : ''})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="ligne-description">
                        <input
                          type="text"
                          placeholder="Nom affiché (modifiable)"
                          title="Vous pouvez modifier librement le nom affiché sur le devis"
                          value={ligne.description}
                          onChange={(e) => updateLigne(index, 'description', e.target.value)}
                          required
                        />
                      </div>
                      <div className="ligne-quantite">
                        <input
                          type="number"
                          min="1"
                          step="0.5"
                          placeholder="Qté"
                          value={ligne.quantite}
                          onChange={(e) => updateLigne(index, 'quantite', e.target.value)}
                        />
                      </div>
                      <div className="ligne-prix">
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Prix HT"
                          value={ligne.prix_unitaire_ht}
                          onChange={(e) => updateLigne(index, 'prix_unitaire_ht', e.target.value)}
                        />
                        <span className="currency">€ HT</span>
                      </div>
                      <div className="ligne-prix-mensuel">
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Abo/mois"
                          value={ligne.prix_mensuel_ht}
                          onChange={(e) => updateLigne(index, 'prix_mensuel_ht', e.target.value)}
                        />
                        <span className="currency">€/mois</span>
                      </div>
                      <div className="ligne-total">
                        {((parseFloat(ligne.prix_unitaire_ht) || 0) * (parseFloat(ligne.quantite) || 1)).toFixed(2)} €
                      </div>
                      <button
                        type="button"
                        className="btn-remove-ligne"
                        onClick={() => removeLigne(index)}
                        disabled={formData.lignes.length === 1}
                      >
                        ×
                      </button>

                      {/* Champs Formation Professionnelle */}
                      {ligne.is_formation && (
                        <div className="formation-fields" style={{
                          width: '100%',
                          backgroundColor: '#f8f4ff',
                          border: '2px solid #6f42c1',
                          borderRadius: '10px',
                          padding: '15px',
                          marginTop: '10px',
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                          gap: '15px'
                        }}>
                          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                            <GraduationCap size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} />
                            <strong style={{ color: '#6f42c1' }}>Formation Professionnelle - Mentions obligatoires</strong>
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Intitulé de la formation *</label>
                            <input
                              type="text"
                              placeholder="Ex: CYBERSECURITE - SENSIBILISATION DES UTILISATEURS"
                              value={ligne.formation_intitule}
                              onChange={(e) => updateLigne(index, 'formation_intitule', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>N° Convention/Contrat</label>
                            <input
                              type="text"
                              placeholder="Ex: CONV-2026-001"
                              value={ligne.formation_convention_numero}
                              onChange={(e) => updateLigne(index, 'formation_convention_numero', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Date convention</label>
                            <input
                              type="date"
                              value={ligne.formation_convention_date}
                              onChange={(e) => updateLigne(index, 'formation_convention_date', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Bénéficiaire(s) *</label>
                            <input
                              type="text"
                              placeholder="Nom du stagiaire ou 'Groupe de X stagiaires'"
                              value={ligne.formation_beneficiaires}
                              onChange={(e) => updateLigne(index, 'formation_beneficiaires', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Modalité *</label>
                            <select
                              value={ligne.formation_modalite}
                              onChange={(e) => updateLigne(index, 'formation_modalite', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            >
                              <option value="">-- Sélectionner --</option>
                              <option value="presentiel">Présentiel</option>
                              <option value="distanciel">Distanciel</option>
                              <option value="mixte">Mixte</option>
                            </select>
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Durée (heures) *</label>
                            <input
                              type="number"
                              min="1"
                              placeholder="Ex: 14"
                              value={ligne.formation_duree_heures}
                              onChange={(e) => updateLigne(index, 'formation_duree_heures', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Date début</label>
                            <input
                              type="date"
                              value={ligne.formation_date_debut}
                              onChange={(e) => updateLigne(index, 'formation_date_debut', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div className="form-group" style={{ margin: 0 }}>
                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>Date fin</label>
                            <input
                              type="date"
                              value={ligne.formation_date_fin}
                              onChange={(e) => updateLigne(index, 'formation_date_fin', e.target.value)}
                              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                          </div>

                          <div style={{ gridColumn: '1 / -1', fontSize: '11px', color: '#6f42c1', fontStyle: 'italic', marginTop: '5px' }}>
                            <Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Mention légale ajoutée : "Formation professionnelle au sens de l'article L.6313-1 du Code du travail"
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Totaux */}
                <div className="devis-totaux-container">
                  <div className="devis-totaux">
                    <h4>Montant ponctuel</h4>
                    <div className="total-row">
                      <span>Total HT</span>
                      <span>{totalHT.toFixed(2)} €</span>
                    </div>
                    <div className="total-row">
                      <span>TVA (20%)</span>
                      <span>{tva.toFixed(2)} €</span>
                    </div>
                    <div className="total-row total-ttc">
                      <span>Total TTC</span>
                      <span>{totalTTC.toFixed(2)} €</span>
                    </div>
                  </div>

                  {totalMensuelHT > 0 && (
                    <div className="devis-totaux devis-totaux-mensuel">
                      <h4>Abonnement mensuel</h4>
                      <div className="total-row">
                        <span>Total HT/mois</span>
                        <span>{totalMensuelHT.toFixed(2)} €</span>
                      </div>
                      <div className="total-row">
                        <span>TVA (20%)</span>
                        <span>{tvaMensuel.toFixed(2)} €</span>
                      </div>
                      <div className="total-row total-ttc">
                        <span>Total TTC/mois</span>
                        <span>{totalMensuelTTC.toFixed(2)} €</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notes et conditions */}
                <div className="form-row">
                  <div className="form-group flex-1">
                    <label>Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Notes internes ou pour le client..."
                      rows="2"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group flex-1">
                    <label>Conditions</label>
                    <textarea
                      value={formData.conditions}
                      onChange={(e) => setFormData({ ...formData, conditions: e.target.value })}
                      placeholder="Conditions de paiement..."
                      rows="2"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingDevis ? 'Enregistrer' : 'Créer le devis'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal visualisation */}
      {viewingDevis && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            <div className="modal-header">
              <h2>Devis {viewingDevis.numero_devis}</h2>
              <button className="modal-close" onClick={() => setViewingDevis(null)}>×</button>
            </div>
            <div className="modal-body devis-preview">
              <div className="preview-header">
                <div className="preview-info">
                  <p><strong>Client:</strong> {viewingDevis.client_nom}</p>
                  {viewingDevis.client_entreprise && <p><strong>Entreprise:</strong> {viewingDevis.client_entreprise}</p>}
                  <p><strong>Email:</strong> {viewingDevis.client_email}</p>
                  <p><strong>Téléphone:</strong> {viewingDevis.client_telephone}</p>
                </div>
                <div className="preview-dates">
                  <p><strong>Date d'émission:</strong> {new Date(viewingDevis.date_emission).toLocaleDateString('fr-FR')}</p>
                  <p><strong>Valide jusqu'au:</strong> {new Date(viewingDevis.date_validite).toLocaleDateString('fr-FR')}</p>
                  <p><strong>Statut:</strong> {getStatutBadge(viewingDevis.statut)}</p>
                </div>
              </div>

              <table className="preview-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Quantité</th>
                    <th>Prix HT</th>
                    <th>Total HT</th>
                    <th>Abo/mois HT</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingDevis.lignes?.map((ligne, i) => (
                    <tr key={i}>
                      <td>{ligne.description}</td>
                      <td>{ligne.quantite}</td>
                      <td>{parseFloat(ligne.prix_unitaire_ht || 0).toFixed(2)} €</td>
                      <td>{parseFloat(ligne.total_ht || 0).toFixed(2)} €</td>
                      <td>{parseFloat(ligne.prix_mensuel_ht || 0) > 0 ? `${parseFloat(ligne.prix_mensuel_ht).toFixed(2)} €` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="preview-totaux-container">
                <div className="preview-totaux">
                  <h4>Montant ponctuel</h4>
                  <div className="total-line">
                    <span>Total HT</span>
                    <span>{parseFloat(viewingDevis.total_ht).toFixed(2)} €</span>
                  </div>
                  <div className="total-line">
                    <span>TVA ({viewingDevis.taux_tva}%)</span>
                    <span>{parseFloat(viewingDevis.total_tva).toFixed(2)} €</span>
                  </div>
                  <div className="total-line total-ttc">
                    <span>Total TTC</span>
                    <span>{parseFloat(viewingDevis.total_ttc).toFixed(2)} €</span>
                  </div>
                </div>

                {parseFloat(viewingDevis.total_mensuel_ht || 0) > 0 && (
                  <div className="preview-totaux preview-totaux-mensuel">
                    <h4>Abonnement mensuel</h4>
                    <div className="total-line">
                      <span>Total HT/mois</span>
                      <span>{parseFloat(viewingDevis.total_mensuel_ht).toFixed(2)} €</span>
                    </div>
                    <div className="total-line">
                      <span>TVA ({viewingDevis.taux_tva}%)</span>
                      <span>{parseFloat(viewingDevis.total_mensuel_tva).toFixed(2)} €</span>
                    </div>
                    <div className="total-line total-ttc">
                      <span>Total TTC/mois</span>
                      <span>{parseFloat(viewingDevis.total_mensuel_ttc).toFixed(2)} €</span>
                    </div>
                  </div>
                )}
              </div>

              {viewingDevis.notes && (
                <div className="preview-notes">
                  <strong>Notes:</strong>
                  <p>{viewingDevis.notes}</p>
                </div>
              )}

              {viewingDevis.conditions && (
                <div className="preview-conditions">
                  <strong>Conditions:</strong>
                  <p>{viewingDevis.conditions}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setViewingDevis(null)}>Fermer</button>
              <button className="btn-primary" onClick={() => downloadPDF(viewingDevis.id, viewingDevis.numero_devis)}>
                Télécharger PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal lien de paiement Stripe */}
      {paymentLinkModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-payment-link">
            <div className="modal-header modal-header-stripe">
              <h2><CreditCard size={20} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Lien de Paiement Stripe</h2>
              <button className="modal-close" onClick={() => setPaymentLinkModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="payment-link-info">
                <p><strong>Devis :</strong> {paymentLinkModal.devisNumero}</p>
                <div className="payment-amounts">
                  {paymentLinkModal.montantPonctuel > 0 && (
                    <div className="amount-badge amount-ponctuel">
                      <span className="amount-label">Paiement immédiat</span>
                      <span className="amount-value">{paymentLinkModal.montantPonctuel.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                    </div>
                  )}
                  {paymentLinkModal.montantMensuel > 0 && (
                    <div className="amount-badge amount-mensuel">
                      <span className="amount-label">Puis chaque mois (à partir de M+1)</span>
                      <span className="amount-value">{paymentLinkModal.montantMensuel.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €/mois</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="payment-link-container">
                <label>Lien de paiement :</label>
                <div className="payment-link-input-group">
                  <input
                    type="text"
                    readOnly
                    value={paymentLinkModal.link}
                    className="payment-link-input"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    className="btn-copy"
                    onClick={() => copyToClipboard(paymentLinkModal.link)}
                  >
                    <Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Copier
                  </button>
                </div>
              </div>

              <div className="payment-link-actions">
                <a
                  href={paymentLinkModal.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-open-link"
                >
                  🔗 Ouvrir le lien
                </a>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setPaymentLinkModal(null)}>Fermer</button>
              <button
                className="btn-primary btn-copy-large"
                onClick={() => copyToClipboard(paymentLinkModal.link)}
              >
                <Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Copier le lien
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevisManagement;
